import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Lock } from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import useApi from "../../utils/useApi";
import apis from "../../utils/apis";
import { setToken } from "../../store/slices/userSlice";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { App as CapacitorApp } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { OtpPurpose } from "../../utils/OtpPurpose";

const LoginPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [phoneNumber, setPhoneNumber] = useState("");
  const [otpSentToNumber, setOtpSentToNumber] = useState(""); // Track which number OTP was sent to
  const [otp, setOtp] = useState("");
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [otpTimeoutError, setOtpTimeoutError] = useState(false);
  const [timeoutCooldown, setTimeoutCooldown] = useState(0);

  const navigate = useNavigate();

  const { callApi: callSendOtp, isLoading: sendOtpLoading } = useApi();
  const { callApi: callVerifyOtp, isLoading: verifyOtpLoading } = useApi();

  // Initialize timeout from localStorage on component mount
  useEffect(() => {
    const storedTimeoutEnd = localStorage.getItem('otpTimeoutEnd');
    if (storedTimeoutEnd) {
      const endTime = parseInt(storedTimeoutEnd, 10);
      const now = Date.now();
      const remainingTime = Math.floor((endTime - now) / 1000);
      
      if (remainingTime > 0) {
        setTimeoutCooldown(remainingTime);
        setOtpTimeoutError(true);
      } else {
        // Timeout has expired, clean up localStorage
        localStorage.removeItem('otpTimeoutEnd');
      }
    }
  }, []);

  const handleSendOtp = async () => {
    // If phone number has changed, reset cooldowns and allow new OTP
    const hasPhoneNumberChanged = otpSentToNumber && phoneNumber !== otpSentToNumber;
    
    if (hasPhoneNumberChanged) {
      // Reset all cooldowns for new number
      setResendCooldown(0);
      setTimeoutCooldown(0);
      setOtpTimeoutError(false);
      localStorage.removeItem('otpTimeoutEnd');
      setOtp(""); // Clear any existing OTP
    }
    
    // Don't send OTP if there's an active cooldown or timeout for the SAME number
    if (!hasPhoneNumberChanged && (resendCooldown > 0 || timeoutCooldown > 0)) {
      return setShowOtpModal(true);
    }
    
    try {
      const res = await callSendOtp({
        api: apis.sendOtp,
        body: { mobile: phoneNumber, purpose: OtpPurpose.ACCOUNT_LOGIN },
      });

      setOtpSentToNumber(phoneNumber); // Store the number OTP was sent to
      setShowOtpModal(true);

      if (res?.code === "OTP_TIMOUT") {
        setOtpTimeoutError(true);
        setTimeoutCooldown(60);
        // Store timeout end time in localStorage
        const timeoutEndTime = Date.now() + 60 * 1000; // 60 seconds from now
        localStorage.setItem('otpTimeoutEnd', timeoutEndTime.toString());
        return;
      }

      setResendCooldown(60);
      setOtpTimeoutError(false); // Reset timeout error on successful send
      setTimeoutCooldown(0); // Reset timeout cooldown
      // Clear timeout from localStorage on successful send
      localStorage.removeItem('otpTimeoutEnd');
      toast.success(t("login.errors.otpSent"));
    } catch (error) {
      // Check if it's an OTP_TIMOUT error
      if (error?.code === "OTP_TIMOUT") {
        setOtpTimeoutError(true);
        setTimeoutCooldown(60); // Start 1 minute countdown
        setShowOtpModal(true); // Show modal with timeout message
        setOtpSentToNumber(phoneNumber); // Store the number even on timeout
        // Store timeout end time in localStorage
        const timeoutEndTime = Date.now() + 60 * 1000; // 60 seconds from now
        localStorage.setItem('otpTimeoutEnd', timeoutEndTime.toString());
      } else {
        toast.error(t("login.errors.otpFailed"));
      }
    }
  };

  const handleVerifyOtp = async () => {
    try {
      if (!otp || otp.length !== 6) {
        toast.error(t("login.errors.invalidOtp"));
        return;
      }

      let res = await callVerifyOtp({
        api: apis.verifyOtp,
        body: { mobile: phoneNumber, otp: otp },
      });

      const token = res?.token;

      localStorage.setItem("token", token);

      setShowOtpModal(false);
      dispatch(setToken(token));
      setTimeout(() => {
        const isFirstTime = !localStorage.getItem("firstLogin")
        if(!isFirstTime) {
          return navigate("/");
        }
        
        localStorage.setItem("firstLogin", "true")
        navigate("/onboarding");
        // navigate("/");
      }, 500);
    } catch (error) {
      toast.error(t("login.errors.otpVerifyFailed"));
    }
  };

  useEffect(() => {
    let timer;
    if (resendCooldown > 0) {
      timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  useEffect(() => {
    let timer;
    if (timeoutCooldown > 0) {
      timer = setTimeout(() => setTimeoutCooldown(timeoutCooldown - 1), 1000);
    } else if (timeoutCooldown === 0 && otpTimeoutError) {
      // Reset timeout error when countdown reaches 0
      setOtpTimeoutError(false);
      // Clear timeout from localStorage when countdown reaches 0
      localStorage.removeItem('otpTimeoutEnd');
    }
    return () => clearTimeout(timer);
  }, [timeoutCooldown, otpTimeoutError]);

  // Close OTP modal if phone number changes
  useEffect(() => {
    if (otpSentToNumber && phoneNumber !== otpSentToNumber) {
      setShowOtpModal(false);
    }
  }, [phoneNumber, otpSentToNumber]);

  // Handle back button for login page
  useEffect(() => {
    // Only register hardware back button listener on native platforms
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    const handleBackButton = CapacitorApp.addListener(
      "backButton",
      () => {
        // If OTP modal is open, close it first
        if (showOtpModal) {
          setShowOtpModal(false);
        } else {
          // If modal is not open, exit the application
          CapacitorApp.exitApp();
        }
      }
    );

    // Cleanup
    return () => {
      handleBackButton.remove();
    };
  }, [showOtpModal]);

  return (
    <div className="grow font-sans flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="bg-white rounded-lg shadow-md p-8 w-full max-w-md section"
      >
        <h1 className="text-3xl font-bold text-primary-100 mb-6 text-center">
        {t("login.title")}
        </h1>
        <div className="space-y-4">
          <div>
            <label
              htmlFor="phone"
              className="text-sm font-medium text-gray-700 mb-1"
            >
              {t("login.phoneLabel")}
            </label>
            <div className="relative">
              <input
                type="tel"
                id="phone"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder={t("login.phonePlaceholder")}
                className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-100 focus:border-transparent"
                maxLength={10}
              />
              <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 size-5" />
            </div>
          </div>
          <button
            onClick={handleSendOtp}
            disabled={sendOtpLoading || phoneNumber.length !== 10}
            className="w-full bg-primary-100 text-white py-2 rounded-lg font-semibold hover:bg-primary-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {sendOtpLoading ? t("login.sendingOtp") : t("login.sendOtp")}
          </button>
        </div>
        <p className="mt-4 text-xs text-gray-500 text-center leading-relaxed">
          {t("login.termsConsentPrefix")}{" "}
          <Link to="/terms" className="text-primary-100 underline hover:no-underline">
            {t("legal.terms")}
          </Link>
          ,{" "}
          <Link to="/privacy" className="text-primary-100 underline hover:no-underline">
            {t("legal.privacy")}
          </Link>
          {" "}{t("login.termsConsentAnd")}{" "}
          <Link to="/refund-policy" className="text-primary-100 underline hover:no-underline">
            {t("legal.refund")}
          </Link>
          .
        </p>
      </motion.div>

      <AnimatePresence>
        {showOtpModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4"
            onClick={() => setShowOtpModal(false)}
          >
            <motion.div
              initial={{ y: "100%", opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: "100%", opacity: 0 }}
              className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md section"
              onClick={e => e?.stopPropagation()}
            >
              <h2 className="text-2xl font-semibold mb-4">{t("login.otpModalTitle")}</h2>
              
              {/* Show timeout warning if there's a timeout error */}
              {otpTimeoutError && timeoutCooldown > 0 && (
                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-yellow-800 font-medium">
                        {t("login.errors.otpTimeout.cannotResend", {
                          minutes: Math.floor(timeoutCooldown / 60),
                          seconds: (timeoutCooldown % 60).toString().padStart(2, '0')
                        })}
                      </p>
                      <p className="text-xs text-yellow-700 mt-1">
                        {t("login.errors.otpTimeout.enterPreviousOtp")}
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="space-y-4">
                <div>
                  <label
                    htmlFor="otp"
                    className="text-sm font-medium text-gray-700 mb-1"
                  >
                    {t("login.otpLabel")}
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      id="otp"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value)}
                      placeholder={t("login.otpPlaceholder")}
                      className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-100 focus:border-transparent"
                      maxLength={6}
                    />
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  </div>
                </div>
                <button
                  onClick={handleVerifyOtp}
                  disabled={verifyOtpLoading || otp.length !== 6}
                  className="w-full bg-primary-100 text-white py-2 rounded-lg font-semibold hover:bg-primary-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {verifyOtpLoading ? t("login.verifying") : t("login.verifyOtp")}
                </button>
                <p className="text-sm text-gray-600 text-center">
                  {(resendCooldown > 0 || timeoutCooldown > 0) ? (
                    <span>{t("login.resendIn", { count: Math.max(resendCooldown, timeoutCooldown) })}</span>
                  ) : (
                    <button
                      onClick={handleSendOtp}
                      className="text-primary-100 hover:text-primary-100 transition-colors"
                    >
                      {t("login.resendOtp")}
                    </button>
                  )}
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LoginPage;
